import {
  ParentLimitedResponse,
  ParentsService,
  StudentLimitedResponse,
  StudentsService,
} from "client/openapi";
import { PersonKind } from "client/openapi/models/PersonKind";
import type { PersonRow } from "client/openapi/models/PersonRow";
import { Dialog, DialogContent } from "components/Dialog";
import Students from "pages/manage/parents/[id]/ParentInfoTab/Students";
import Parents from "pages/manage/students/[id]/StudentInfoTab/Parents";
import { useEffect, useState } from "react";
import Notifications from "util/notifications";

/**
 * "No parent linked" / "No student linked": link one here, with the same
 * section the student's or parent's page links with. The list reloads once
 * the window closes, if anyone was linked.
 */
export default function LinkFamilyDialog({
  row,
  orgId,
  onClose,
  onChange,
}: {
  row: PersonRow;
  orgId: number;
  onClose: () => void;
  /** Someone was linked. */
  onChange: () => void;
}) {
  const student = row.kind === PersonKind.STUDENT;
  const [options, setOptions] = useState<
    ParentLimitedResponse[] | StudentLimitedResponse[]
  >([]);
  const [linked, setLinked] = useState<
    ParentLimitedResponse[] | StudentLimitedResponse[]
  >([]);

  useEffect(() => {
    (student
      ? ParentsService.getParentsByOrganization({ orgId })
      : StudentsService.getStudentsByOrganizationIfAdmin({ orgId })
    )
      .then(setOptions)
      .catch(() => Notifications.error("Could not load the list to link."));
  }, [orgId, student]);

  function close() {
    if (linked.length > 0) onChange();
    onClose();
  }

  const person = { ...row, org_id: orgId };
  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="dialog-content max-w-[520px]">
        {student ? (
          <Parents
            student={person as unknown as StudentLimitedResponse}
            orgParents={options as ParentLimitedResponse[]}
            studentParents={linked as ParentLimitedResponse[]}
            setStudentParents={setLinked}
          />
        ) : (
          <Students
            parent={person as unknown as ParentLimitedResponse}
            orgStudents={options as StudentLimitedResponse[]}
            parentStudents={linked as StudentLimitedResponse[]}
            setParentStudents={setLinked}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
